import { useState, useEffect, useRef } from "react";
import axios from "axios";
import css from "../index.module.css";

import { toast } from "sonner";
import useUserData from "@hooks/useUserData";

interface AddressSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    postal_code?: string;
    country: string;
    region_with_type: string;
    city_with_type?: string;
    settlement_with_type?: string;
    street_with_type?: string;
    house?: string;
    flat?: string;
    geo_lat?: string;
    geo_lon?: string;
  };
}

export default function Addresses({ page, setPage }: { page: number, setPage: (page: number) => void }) {
  const { refreshUserData } = useUserData();
  const [addresses, setAddresses] = useState<any>([]);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] =
    useState<AddressSuggestion | null>(null);
  const addressDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastRequestedQuery = useRef<string>("");
  const addressInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_API_URL}/addresses`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setAddresses(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // Debounced address search
  useEffect(() => {
    if (addressDebounceTimer.current) {
      clearTimeout(addressDebounceTimer.current);
      addressDebounceTimer.current = null;
    }

    const trimmedQuery = addressQuery.trim();

    // Don't search if query is too short or same as last request
    if (trimmedQuery.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      lastRequestedQuery.current = "";
      return;
    }

    const isNewQuery = trimmedQuery !== lastRequestedQuery.current;

    if (isNewQuery && page === 4) {
      addressDebounceTimer.current = setTimeout(() => {
        const currentQuery = addressQuery.trim();
        const stillValid = currentQuery.length >= 3;
        const stillNew = currentQuery !== lastRequestedQuery.current;

        if (stillValid && stillNew && page === 4) {
          lastRequestedQuery.current = currentQuery;
          setIsLoadingSuggestions(true);
          setShowSuggestions(true);

          axios
            .get(
              `${
                import.meta.env.VITE_API_URL
              }/dadata/findAddress?query=${encodeURIComponent(currentQuery)}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
              }
            )
            .then((response) => {
              setAddressSuggestions(response.data || []);
              setIsLoadingSuggestions(false);
            })
            .catch((error) => {
              console.log(error);
              setAddressSuggestions([]);
              setIsLoadingSuggestions(false);
            });
        }
      }, 400); // 400ms debounce for better mobile UX
    }

    return () => {
      if (addressDebounceTimer.current) {
        clearTimeout(addressDebounceTimer.current);
      }
    };
  }, [addressQuery, page]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showSuggestions]);

  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    const selectedAddress = suggestion.unrestricted_value || suggestion.value;
    setAddressQuery(selectedAddress);
    setSelectedAddressSuggestion(suggestion);
    setAddressSuggestions([]);
    setShowSuggestions(false);
    lastRequestedQuery.current = selectedAddress;
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    // Handle address addition separately
    if (page === 4) {
      // Validate that address was selected from suggestions
      if (!selectedAddressSuggestion) {
        toast.error("Пожалуйста, выберите адрес из списка подсказок");
        return;
      }

      // Validate that current address matches the selected suggestion
      const currentAddress = addressQuery.trim();
      const selectedAddress =
        selectedAddressSuggestion.unrestricted_value ||
        selectedAddressSuggestion.value;

      if (currentAddress !== selectedAddress) {
        toast.error(
          "Адрес был изменен вручную. Пожалуйста, выберите адрес из списка подсказок"
        );
        return;
      }

      const addressData = {
        title: formData.get("title") || "",
        address: selectedAddress,
      };

      axios
        .post(`${import.meta.env.VITE_API_URL}/addresses`, addressData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((_) => {
          toast.success("Адрес успешно добавлен");
          // Reset form state
          setAddressQuery("");
          setAddressSuggestions([]);
          setShowSuggestions(false);
          setSelectedAddressSuggestion(null);
          lastRequestedQuery.current = "";
          // Refresh addresses list
          axios
            .get(`${import.meta.env.VITE_API_URL}/addresses`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              },
            })
            .then((response) => {
              setAddresses(response.data);
              setPage(3); // Go back to addresses list
            })
            .catch((error) => {
              console.log(error);
            });
        })
        .catch((_) => {
          toast.error("Введите название магазина!");
        });
      return;
    }

    // Handle user data update
    axios
      .post(`${import.meta.env.VITE_API_URL}/user`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((_) => {
        toast.success("Данные успешно обновлены");
        refreshUserData();
      })
      .catch((_) => {
        toast.error("Ошибка обновления данных");
      });
  };

  // Reset address state when switching pages
  useEffect(() => {
    if (page !== 4) {
      setAddressQuery("");
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setSelectedAddressSuggestion(null);
      lastRequestedQuery.current = "";
    }
  }, [page]);

  return (
    <>
    {page === 3 && (
    <div className={css.col1}>
        <div className={css.accountInfo}>
            <button
            type="button"
            style={{ width: "100%", marginBottom: 10 }}
            onClick={() => setPage(4)}
            >
            Добавить адрес
            </button>

            {addresses.map((item: any) => (
            <div key={item.id} className={css.addressItem}>
                <h3>{item.title}</h3>
                <p>{item.address}</p>
            </div>
            ))}

            {addresses.length <= 0 && (
            <a style={{ fontSize: 18 }}>адресов нет...</a>
            )}
        </div>
    </div>
    )}
    {page === 4 && (
    <div className={css.col1}>
        <div className={css.accountInfo}>
            <form className={css.form} onSubmit={handleSubmit}>
            <div className={css.flex}>
                <b className={css.headAcc}>Добавить адрес</b>
                <input
                type="text"
                name="title"
                placeholder="Название магазина"
                />
                <div style={{ position: "relative", width: "100%" }}>
                <input
                    ref={addressInputRef}
                    type="text"
                    name="address"
                    placeholder="Адрес"
                    value={addressQuery}
                    onChange={(e) => {
                    setAddressQuery(e.target.value);
                    // Reset selected suggestion if user manually edits the address
                    setSelectedAddressSuggestion(null);
                    if (e.target.value.trim().length >= 3) {
                        setShowSuggestions(true);
                    }
                    }}
                    onFocus={() => {
                    if (
                        addressQuery.trim().length >= 3 &&
                        addressSuggestions.length > 0
                    ) {
                        setShowSuggestions(true);
                    }
                    }}
                    autoComplete="off"
                    style={{ width: "100%" }}
                />
                {showSuggestions && (
                    <div
                    ref={suggestionsRef}
                    className={css.addressSuggestions}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        marginTop: "4px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                    }}
                    >
                    {isLoadingSuggestions ? (
                        <div
                        style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "#6b7280",
                        }}
                        >
                        Поиск...
                        </div>
                    ) : addressSuggestions.length > 0 ? (
                        addressSuggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className={css.addressSuggestionItem}
                            onClick={(e) => {
                            e.preventDefault();
                            handleAddressSelect(suggestion);
                            }}
                            onTouchEnd={(e) => {
                            e.preventDefault();
                            handleAddressSelect(suggestion);
                            }}
                            style={{
                            padding: "14px 16px",
                            borderBottom: "1px solid #f3f4f6",
                            cursor: "pointer",
                            backgroundColor: "#fff",
                            transition: "background-color 0.15s",
                            fontSize: "16px",
                            lineHeight: "1.4",
                            }}
                            onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "#f9fafb";
                            }}
                            onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#fff";
                            }}
                        >
                            <div
                            style={{
                                fontWeight: 500,
                                color: "#111827",
                                marginBottom: "2px",
                            }}
                            >
                            {suggestion.value}
                            </div>
                            {suggestion.unrestricted_value &&
                            suggestion.unrestricted_value !==
                                suggestion.value && (
                                <div
                                style={{
                                    fontSize: "14px",
                                    color: "#6b7280",
                                }}
                                >
                                {suggestion.unrestricted_value}
                                </div>
                            )}
                        </div>
                        ))
                    ) : addressQuery.trim().length >= 3 ? (
                        <div
                        style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "#6b7280",
                        }}
                        >
                        Адреса не найдены
                        </div>
                    ) : null}
                    </div>
                )}
                </div>
            </div>
            <button>Сохранить адрес</button>
            </form>
        </div>
    </div>
    )}
    </>
  );
}
